import { MagneticButton } from '@/components/MagneticButton'
import { Icon } from '@/components/Icon'
import { PageShell } from '@/components/ui/site-chrome'
import { SectionHeading, Chip } from '@/components/lc'
import { forHosts } from '@/data/content'
import type { IconName } from '@/data/content'
import { LIFECRAFTER_AUTH } from '@/lib/auth'

/* ================================================================== */
/*  LifeCrafter Program: the host side of LifeCraft4U.                   */
/* ================================================================== */

const STEPS = [
  { n: '01', title: 'Apply with your craft', body: 'Tell us what you teach and how long you have practised it. Depth matters more than follower counts.' },
  { n: '02', title: 'Get verified', body: 'Identity and credential checks, plus a conversation with our team. Trust is the whole point.' },
  { n: '03', title: 'Open your circle', body: 'Set up programs, events and consultations in minutes. We handle payments, RSVPs and the boring parts.' },
]

const FORMATS = [
  { icon: 'sun' as IconName, title: 'Daily programs', body: 'Recurring group sessions: morning yoga, riyaaz check-ins, guided sits. A steady rhythm, a steady income.' },
  { icon: 'calendar' as IconName, title: 'Events & workshops', body: 'One-off circles, online or near you. Ticketed simply, seats managed for you.' },
  { icon: 'users' as IconName, title: '1-1 consultations', body: 'Daily windows and weekend slots for personal sessions, priced the way you choose.' },
]

export default function LifeCrafterProgram() {
  return (
    <PageShell>
      {/* hero */}
      <section className="px-5 pb-16 pt-36 md:pt-44">
        <div className="container-lc grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <p className="label flex items-center gap-2 text-lc-mute">
              <Icon name="mic" size={13} /> {forHosts.eyebrow}
            </p>
            <h1 className="mt-4 font-display text-5xl">
              Your craft deserves a{' '}
              <span className="inline-block -rotate-2 rounded-[0.28em] bg-coral px-[0.2em] pb-[0.06em] text-white">community</span>.
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-lc-mute">{forHosts.body}</p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <MagneticButton href={LIFECRAFTER_AUTH} data-cursor="apply" className="rounded-pill bg-lime px-6 py-3 text-[15px] xl:px-8 xl:py-4 xl:text-[17px] font-bold text-night">
                {forHosts.cta}
              </MagneticButton>
              <span className="label text-lc-mute">Members join circles. LifeCrafters host them.</span>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="lc-glass relative overflow-hidden rounded-card p-5 xl:p-7">
              <span className="blob absolute -right-12 -top-12 h-40 w-40 opacity-30" style={{ background: '#FF6B5C' }} aria-hidden="true" />
              <div className="relative">
                <p className="label text-lc-mute">What you get</p>
                <ul className="mt-4 space-y-3">
                  {forHosts.points.map((p) => (
                    <li key={p} className="flex items-center gap-3 text-[15px] xl:text-[16px] font-medium text-lc-ink">
                      <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-lime" aria-hidden="true">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#131118" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"><path d="m4.5 12.5 5 5 10-11" /></svg>
                      </span>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* steps */}
      <section className="px-5 pb-20">
        <div className="container-lc">
          <SectionHeading kicker="How it works" title="Three steps to your first circle." center />
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {STEPS.map((s) => (
              <div key={s.n} className="lc-glass rounded-card p-5 xl:p-7">
                <span className="font-display text-5xl text-lc-mute/50">{s.n}</span>
                <h3 className="mt-4 font-display text-2xl">{s.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-lc-mute">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* formats */}
      <section className="px-5 pb-20">
        <div className="container-lc">
          <SectionHeading kicker="Ways to host" title="Programs, events, consultations." center />
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {FORMATS.map((f) => (
              <div key={f.title} className="lc-glass-soft rounded-card p-6">
                <span className="lc-glass grid h-11 w-11 place-items-center rounded-tile text-lc-accent">
                  <Icon name={f.icon} size={20} />
                </span>
                <h3 className="mt-5 font-display text-xl">{f.title}</h3>
                <p className="mt-2 text-[14px] leading-relaxed text-lc-mute">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* membership — promotional phase: pricing hidden, free enrolment */}
      <section className="px-5 pb-20">
        <div className="container-lc">
          <SectionHeading
            kicker="Membership"
            title="Founding LifeCrafters join free."
            intro="We’re onboarding our first crafters by expression of interest, with no membership fees this phase. Apply and we’ll help you set up your circle."
            center
          />
          <div className="mx-auto mt-10 max-w-2xl">
            <div className="lc-glass relative overflow-hidden rounded-card p-8 text-center ring-2 ring-lime/60 md:p-10">
              <span className="blob absolute -right-12 -top-12 h-40 w-40 opacity-25" style={{ background: '#B6F09C' }} aria-hidden="true" />
              <div className="relative">
                <span className="label inline-block rounded-pill bg-lime px-3 py-1 !text-[11px] text-night">Promotional offer</span>
                <p className="mt-5 text-[15px] text-lc-mute">
                  <span className="line-through decoration-coral/70 decoration-2">₹499 – ₹1,499 / month</span>
                </p>
                <p className="mt-1 font-display text-4xl">Join for free</p>
                <p className="mx-auto mt-4 max-w-md text-[15px] leading-relaxed text-lc-mute">
                  During this launch phase there are no fees to host on LifeCraft4U. Submit your
                  expression of interest and our team will get you started.
                </p>
                <MagneticButton
                  href={LIFECRAFTER_AUTH}
                  data-cursor="apply"
                  className="mt-7 inline-flex rounded-pill bg-lime px-6 py-3 text-[15px] xl:px-8 xl:py-3.5 xl:text-[16px] font-bold text-night"
                >
                  Become a LifeCrafter
                </MagneticButton>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* closing */}
      <section className="px-5 pb-28">
        <div className="container-lc">
          <div className="lc-glass relative overflow-hidden rounded-[36px] px-6 py-16 text-center md:py-20">
            <span className="blob absolute left-1/2 top-0 h-64 w-64 -translate-x-1/2 opacity-35" style={{ background: '#FF6B5C' }} aria-hidden="true" />
            <div className="relative">
              <h2 className="mx-auto max-w-[18ch] font-display text-4xl">Teach what you love. We handle the rest.</h2>
              <MagneticButton href="/contact" data-cursor="apply" className="mt-8 inline-flex rounded-pill bg-lime px-6 py-3 text-[15px] xl:px-9 xl:py-4 xl:text-[18px] font-bold text-night">
                Apply to become a LifeCrafter
              </MagneticButton>
              <div className="mt-6"><Chip tone="outline">Applications reviewed by humans, within a week</Chip></div>
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  )
}
